import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ClientHeaderNavigation from '../../components/ui/ClientHeaderNavigation';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { usePaymentConfirmation } from '../../hooks/usePaymentConfirmation';
import repositoryContainer from '../../services/RepositoryContainer';

const pendingRequestKey = (companyPath) => `clawfe:pendingRequest:${companyPath || 'global'}`;

const readPendingRequest = (companyPath) => {
  try {
    const stored = localStorage.getItem(pendingRequestKey(companyPath));
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const RequestPaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { companyPath } = useParams();
  const reference = searchParams.get('reference') || searchParams.get('trxref');
  const [pendingRequest] = useState(() => readPendingRequest(companyPath));
  const [stage, setStage] = useState('verifying');
  const [copied, setCopied] = useState(false);
  const verifyAttempted = useRef(false);

  useEffect(() => {
    if (!reference || verifyAttempted.current) return;
    verifyAttempted.current = true;

    const verify = async () => {
      try {
        const verification = await repositoryContainer.bookingRequest.verifyPayment(reference);
        const verified = typeof verification === 'boolean'
          ? verification
          : verification?.success ?? verification?.response?.success;
        if (verified === false) throw new Error('Payment is not confirmed yet.');
        if (pendingRequest?.referenceCode) {
          await repositoryContainer.bookingRequest.getByReferenceCode(pendingRequest.referenceCode).catch(() => null);
        }
        setStage('confirmed');
      } catch {
        setStage('waiting');
      }
    };

    verify();
  }, [reference]);

  const { status: signalRStatus, timeoutReached } = usePaymentConfirmation(
    stage === 'waiting' ? reference : null,
    async () => {
      if (pendingRequest?.referenceCode) {
        await repositoryContainer.bookingRequest.getByReferenceCode(pendingRequest.referenceCode).catch(() => null);
      }
      setStage('confirmed');
    }
  );

  useEffect(() => {
    if (signalRStatus === 'failed') setStage('failed');
  }, [signalRStatus]);

  useEffect(() => {
    if (stage !== 'confirmed') return;
    try {
      const current = readPendingRequest(companyPath);
      if (!current?.paymentReference || current.paymentReference === reference) {
        localStorage.removeItem(pendingRequestKey(companyPath));
      }
    } catch {
      // The confirmed state remains valid even when browser storage is unavailable.
    }
  }, [companyPath, reference, stage]);

  const requestReference = pendingRequest?.referenceCode;
  const storePath = companyPath ? `/${companyPath}` : '/discover';
  const trackRequest = () => {
    if (requestReference) {
      navigate(`/track/${encodeURIComponent(requestReference)}`, { replace: true });
      return;
    }
    navigate(storePath, { replace: true });
  };

  const copyRequestReference = async () => {
    if (!requestReference) return;
    try {
      await navigator.clipboard.writeText(requestReference);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  if (!reference) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="max-w-sm space-y-4 text-center">
          <Icon name="AlertCircle" size={44} className="mx-auto text-destructive" />
          <h1 className="font-heading text-xl font-bold text-foreground">Invalid payment link</h1>
          <p className="text-sm text-muted-foreground font-caption">No payment reference was provided.</p>
          <Button onClick={trackRequest}>{requestReference ? 'Track Request' : 'Back to Store'}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <ClientHeaderNavigation />
      <main className="flex min-h-[calc(100vh-80px)] items-center justify-center px-4 pb-10 pt-24">
        <div className="w-full max-w-md space-y-6 text-center">
          {stage === 'verifying' && (
            <>
              <div className="mx-auto h-20 w-20 animate-spin rounded-full border-4 border-primary/20 border-t-primary" />
              <div>
                <h1 className="font-heading text-2xl font-bold text-foreground">Verifying payment</h1>
                <p className="mt-2 text-sm text-muted-foreground font-caption">Confirming payment for your request.</p>
              </div>
            </>
          )}

          {stage === 'waiting' && (
            <>
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-warning/10 text-warning">
                <Icon name="Smartphone" size={36} />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-bold text-foreground">Approve on your phone</h1>
                <p className="mt-2 text-sm text-muted-foreground font-caption">
                  Approve the Mobile Money prompt to complete your request payment.
                </p>
              </div>
              <div className="rounded-xl bg-muted/30 p-4">
                <div className="flex items-center justify-center gap-2 text-sm text-warning font-caption">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-warning" />
                  {timeoutReached ? 'Confirmation is taking longer than expected' : 'Waiting for confirmation'}
                </div>
                {requestReference && <p className="mt-3 font-mono text-sm font-semibold text-foreground">{requestReference}</p>}
              </div>
              {timeoutReached && (
                <Button variant="outline" fullWidth onClick={trackRequest}>Check Request Status</Button>
              )}
            </>
          )}

          {stage === 'confirmed' && (
            <>
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-success/10 text-success">
                <Icon name="CheckCircle" size={40} />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-bold text-foreground">Payment confirmed</h1>
                <p className="mt-2 text-sm text-muted-foreground font-caption">
                  Your request and payment have been received successfully.
                </p>
              </div>
              {requestReference && (
                <button
                  type="button"
                  onClick={copyRequestReference}
                  className="w-full rounded-xl bg-muted/30 p-4 transition-colors hover:bg-muted/50"
                >
                  <span className="block text-xs uppercase tracking-wide text-muted-foreground font-caption">Request reference</span>
                  <span className="mt-2 flex items-center justify-center gap-2 font-mono text-xl font-bold text-primary">
                    {requestReference}
                    <Icon name={copied ? 'Check' : 'Copy'} size={17} />
                  </span>
                  <span className="mt-1 block text-xs text-muted-foreground font-caption">{copied ? 'Copied' : 'Click to copy'}</span>
                </button>
              )}
              <Button fullWidth onClick={trackRequest} iconName="ArrowRight" iconPosition="right">
                {requestReference ? 'Track Request' : 'Back to Store'}
              </Button>
            </>
          )}

          {stage === 'failed' && (
            <>
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                <Icon name="XCircle" size={40} />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-bold text-foreground">Confirmation unavailable</h1>
                <p className="mt-2 text-sm text-muted-foreground font-caption">
                  We could not listen for payment confirmation. You can still check the request status safely.
                </p>
              </div>
              <Button fullWidth onClick={trackRequest}>{requestReference ? 'Track Request' : 'Back to Store'}</Button>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default RequestPaymentCallback;
